import {
  doc,
  collection,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { getDb } from './config';
import {
  FirestoreRoom,
  RoomParticipant,
  ParticipantStatus,
  RoomStatus,
  CreateRoomInput,
  IncomingCall,
  getCollectionPath,
} from './types';

/**
 * Create a new room document
 */
export async function createRoom(input: CreateRoomInput): Promise<void> {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());
  const roomRef = doc(roomsCollection, input.room_id);

  // Create initial participants array with creator and target user
  const participants: RoomParticipant[] = [
    {
      user_uid: input.created_by,
      name: input.created_by_name,
      profile_picture: input.created_by_profile_picture,
      status: 'connecting',
      joined_at: null,
      left_at: null,
    },
    {
      user_uid: input.target_user.user_uid,
      name: input.target_user.name,
      profile_picture: input.target_user.profile_picture,
      status: 'invited',
      joined_at: null,
      left_at: null,
    },
  ];

  const roomData = {
    room_id: input.room_id,
    created_at: serverTimestamp(),
    ended_at: null,
    duration_seconds: null,
    status: 'active' as RoomStatus,
    created_by: input.created_by,
    created_by_name: input.created_by_name,
    team_uid: input.team_uid,
    participants,
  };

  await setDoc(roomRef, roomData);
}

/**
 * Get a room by ID
 */
export async function getRoomById(roomId: string): Promise<FirestoreRoom | null> {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());
  const roomRef = doc(roomsCollection, roomId);
  const roomSnap = await getDoc(roomRef);

  if (roomSnap.exists()) {
    return roomSnap.data() as FirestoreRoom;
  }
  return null;
}

/**
 * Update room status
 */
export async function updateRoomStatus(roomId: string, status: RoomStatus): Promise<void> {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());
  const roomRef = doc(roomsCollection, roomId);

  await updateDoc(roomRef, { status });
}

/**
 * Update a participant's status in the room
 */
export async function updateParticipantStatus(
  roomId: string,
  userUid: string,
  status: ParticipantStatus
): Promise<void> {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());
  const roomRef = doc(roomsCollection, roomId);

  // Get current room data
  const roomSnap = await getDoc(roomRef);
  if (!roomSnap.exists()) {
    console.error(`Room ${roomId} not found`);
    return;
  }

  const roomData = roomSnap.data() as FirestoreRoom;
  const participants = [...roomData.participants];

  // Find and update the participant
  const participantIndex = participants.findIndex((p) => p.user_uid === userUid);
  if (participantIndex === -1) {
    console.error(`Participant ${userUid} not found in room ${roomId}`);
    return;
  }

  // Update status and timestamps
  participants[participantIndex] = {
    ...participants[participantIndex],
    status,
    ...(status === 'on_call' && !participants[participantIndex].joined_at
      ? { joined_at: Timestamp.now() }
      : {}),
    ...(status === 'left' ? { left_at: Timestamp.now() } : {}),
  };

  await updateDoc(roomRef, { participants });
}

/**
 * Add a new participant to the room
 */
export async function addParticipant(
  roomId: string,
  participant: Omit<RoomParticipant, 'joined_at' | 'left_at'>
): Promise<void> {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());
  const roomRef = doc(roomsCollection, roomId);

  const roomSnap = await getDoc(roomRef);
  if (!roomSnap.exists()) {
    console.error(`Room ${roomId} not found`);
    return;
  }

  const roomData = roomSnap.data() as FirestoreRoom;
  const participants = [...roomData.participants];

  // Check if participant already exists
  const existingIndex = participants.findIndex((p) => p.user_uid === participant.user_uid);
  if (existingIndex !== -1) {
    // Update existing participant
    participants[existingIndex] = {
      ...participants[existingIndex],
      status: participant.status,
    };
  } else {
    // Add new participant
    participants.push({
      ...participant,
      joined_at: null,
      left_at: null,
    });
  }

  await updateDoc(roomRef, { participants });
}

/**
 * End a room and calculate duration
 */
export async function endRoom(roomId: string): Promise<void> {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());
  const roomRef = doc(roomsCollection, roomId);

  const roomSnap = await getDoc(roomRef);
  if (!roomSnap.exists()) {
    console.error(`Room ${roomId} not found`);
    return;
  }

  const roomData = roomSnap.data() as FirestoreRoom;
  const endedAt = Timestamp.now();

  // Calculate duration in seconds
  const createdAt = roomData.created_at as Timestamp;
  const durationSeconds = Math.round((endedAt.toMillis() - createdAt.toMillis()) / 1000);

  await updateDoc(roomRef, {
    status: 'ended' as RoomStatus,
    ended_at: endedAt,
    duration_seconds: durationSeconds,
  });
}

/**
 * Get rooms where a user is a participant
 */
export async function getRoomsByUser(userUid: string, limit: number = 50): Promise<FirestoreRoom[]> {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());

  // Query rooms ordered by creation time (descending)
  // Note: Firestore can't directly query array of objects by nested field
  // So we fetch recent rooms and filter client-side
  const q = query(
    roomsCollection,
    orderBy('created_at', 'desc')
  );

  const querySnapshot = await getDocs(q);
  const rooms: FirestoreRoom[] = [];

  querySnapshot.forEach((docSnap) => {
    const room = docSnap.data() as FirestoreRoom;
    // Check if user is a participant
    const isParticipant = room.participants.some((p) => p.user_uid === userUid);
    if (isParticipant && rooms.length < limit) {
      rooms.push(room);
    }
  });

  return rooms;
}

/**
 * Get all active rooms
 */
export async function getActiveRooms(): Promise<FirestoreRoom[]> {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());

  const q = query(
    roomsCollection,
    where('status', '==', 'active'),
    orderBy('created_at', 'desc')
  );

  const querySnapshot = await getDocs(q);
  const rooms: FirestoreRoom[] = [];

  querySnapshot.forEach((docSnap) => {
    rooms.push(docSnap.data() as FirestoreRoom);
  });

  return rooms;
}

/**
 * Get rooms by team
 */
export async function getRoomsByTeam(teamUid: string, limit: number = 50): Promise<FirestoreRoom[]> {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());

  const q = query(
    roomsCollection,
    where('team_uid', '==', teamUid),
    orderBy('created_at', 'desc')
  );

  const querySnapshot = await getDocs(q);
  const rooms: FirestoreRoom[] = [];

  querySnapshot.forEach((docSnap) => {
    if (rooms.length < limit) {
      rooms.push(docSnap.data() as FirestoreRoom);
    }
  });

  return rooms;
}

/**
 * Subscribe to incoming calls for a user
 * Listens for active rooms where the user has status 'invited'
 * Returns an unsubscribe function
 */
export function subscribeToIncomingCalls(
  userUid: string,
  onIncomingCall: (call: IncomingCall | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());

  // Query for active rooms
  const q = query(
    roomsCollection,
    where('status', '==', 'active'),
    orderBy('created_at', 'desc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      console.log('[subscribeToIncomingCalls] Query returned', snapshot.docs.length, 'active rooms');

      // Find rooms where user is invited
      let incomingCall: IncomingCall | null = null;

      for (const docSnap of snapshot.docs) {
        const room = docSnap.data() as FirestoreRoom;
        console.log('[subscribeToIncomingCalls] Checking room:', room.room_id, 'participants:', room.participants.map(p => ({ uid: p.user_uid, status: p.status })));

        // Check if user is a participant with 'invited' status
        const participant = room.participants.find(
          (p) => p.user_uid === userUid && p.status === 'invited'
        );

        if (participant) {
          console.log('[subscribeToIncomingCalls] Found incoming call in room:', room.room_id);
          // Found an incoming call - get caller info
          const caller = room.participants.find(
            (p) => p.user_uid === room.created_by
          );

          incomingCall = {
            room_id: room.room_id,
            caller_uid: room.created_by,
            caller_name: room.created_by_name,
            caller_profile_picture: caller?.profile_picture || null,
            created_at: room.created_at,
          };
          break; // Only handle the most recent incoming call
        }
      }

      onIncomingCall(incomingCall);
    },
    (error) => {
      console.error('[subscribeToIncomingCalls] Error:', error);
      onError?.(error);
    }
  );
}

/**
 * Decline an incoming call
 * Updates the participant status to 'declined' and ends the room
 */
export async function declineCall(roomId: string, userUid: string): Promise<void> {
  await updateParticipantStatus(roomId, userUid, 'declined');
  // End the room since the call was declined
  await endRoom(roomId);
}

/**
 * Subscribe to a specific room's updates
 * Used by caller to watch for target user's response
 */
export function subscribeToRoom(
  roomId: string,
  onRoomUpdate: (room: FirestoreRoom | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const db = getDb();
  const roomsCollection = collection(db, getCollectionPath.rooms());
  const roomRef = doc(roomsCollection, roomId);

  return onSnapshot(
    roomRef,
    (docSnap) => {
      if (docSnap.exists()) {
        onRoomUpdate(docSnap.data() as FirestoreRoom);
      } else {
        onRoomUpdate(null);
      }
    },
    (error) => {
      console.error('[subscribeToRoom] Error:', error);
      onError?.(error);
    }
  );
}

/**
 * Cancel an outgoing call
 * Ends the room if it was just created
 */
export async function cancelOutgoingCall(roomId: string): Promise<void> {
  await endRoom(roomId);
}
